/**
 * WCAG contrast checking for the Customize page.
 *
 * This exists because the reverted palette project shipped six palettes whose
 * muted text failed AA in every one of them, and nothing in the authoring flow
 * said so - the failure was discovered by reading a CSS comment months later.
 * A page that lets people invent color pairs has to tell them when a pair is
 * unreadable, at the moment they choose it.
 */

export type ContrastLevel = 'AAA' | 'AA' | 'AA-large' | 'fail';

/** WCAG 2.1 thresholds. */
export const AA_NORMAL = 4.5;
export const AA_LARGE = 3;
export const AAA_NORMAL = 7;

/** Parse an `R G B` triplet. Returns null for anything else. */
export function parseTriplet(value: string): [number, number, number] | null {
  const m = /^\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*$/.exec(value ?? '');
  if (!m) return null;
  const rgb = [Number(m[1]), Number(m[2]), Number(m[3])] as [number, number, number];
  return rgb.every((c) => c >= 0 && c <= 255) ? rgb : null;
}

/** Relative luminance, per WCAG 2.1. */
export function luminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Contrast ratio between two `R G B` triplets, 1 to 21.
 * Returns null when either value is not a triplet, so callers can stay quiet
 * rather than reporting a bogus ratio for a length or a keyword.
 */
export function contrastRatio(a: string, b: string): number | null {
  const c1 = parseTriplet(a);
  const c2 = parseTriplet(b);
  if (!c1 || !c2) return null;
  const l1 = luminance(c1);
  const l2 = luminance(c2);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/** Grade a ratio. `large` is 18.66px+ bold or 24px+ regular. */
export function grade(ratio: number, large = false): ContrastLevel {
  if (large) {
    if (ratio >= AAA_NORMAL) return 'AAA';
    return ratio >= AA_LARGE ? 'AA' : 'fail';
  }
  if (ratio >= AAA_NORMAL) return 'AAA';
  if (ratio >= AA_NORMAL) return 'AA';
  if (ratio >= AA_LARGE) return 'AA-large';
  return 'fail';
}

export interface ContrastCheck {
  /** Human label for the pair, e.g. 'Body text on panel'. */
  label: string;
  foreground: string;
  background: string;
  ratio: number;
  level: ContrastLevel;
  /** True when the pair is unreadable at normal text sizes. */
  failing: boolean;
}

/** The pairs worth checking: what the app actually puts on top of what. */
export const CONTRAST_PAIRS: { label: string; fg: string; bg: string; large?: boolean }[] = [
  { label: 'Body text on page', fg: 'sem-text-body', bg: 'sem-surface-canvas' },
  { label: 'Body text on panel', fg: 'sem-text-body', bg: 'sem-surface-panel' },
  { label: 'Muted text on panel', fg: 'sem-text-muted', bg: 'sem-surface-panel' },
  { label: 'Label text on panel', fg: 'sem-text-label', bg: 'sem-surface-panel' },
  { label: 'Heading on panel', fg: 'sem-text-heading', bg: 'sem-surface-panel', large: true },
  { label: 'Text on chrome', fg: 'sem-text-body', bg: 'sem-surface-chrome' },
  { label: 'Header text', fg: 'sem-header-fg', bg: 'sem-header-bg' },
  { label: 'Text on accent fill', fg: 'sem-accent-fg', bg: 'sem-accent' },
  { label: 'Link text on panel', fg: 'sem-accent-text', bg: 'sem-surface-panel' },
  { label: 'Tooltip text', fg: 'sem-text-on-overlay', bg: 'sem-surface-overlay' },
  { label: 'Success text', fg: 'sem-ok-fg', bg: 'sem-ok-bg' },
  { label: 'Warning text', fg: 'sem-warn-fg', bg: 'sem-warn-bg' },
  { label: 'Error text', fg: 'sem-danger-fg', bg: 'sem-danger-bg' },
  { label: 'Info text', fg: 'sem-info-fg', bg: 'sem-info-bg' },
  { label: 'Changed-field text', fg: 'sem-flag-fg', bg: 'sem-flag-bg' },
];

/**
 * Check every pair, given a resolver that turns a token name into a triplet.
 * Pairs whose values cannot be resolved are skipped rather than reported as
 * failures - an unknown token is a different problem from a low-contrast one.
 */
export function checkContrast(resolve: (token: string) => string): ContrastCheck[] {
  const out: ContrastCheck[] = [];
  for (const pair of CONTRAST_PAIRS) {
    const foreground = resolve(pair.fg);
    const background = resolve(pair.bg);
    const ratio = contrastRatio(foreground, background);
    if (ratio === null) continue;
    const level = grade(ratio, pair.large);
    out.push({
      label: pair.label,
      foreground,
      background,
      ratio: Math.round(ratio * 100) / 100,
      level,
      failing: level === 'fail' || level === 'AA-large',
    });
  }
  return out;
}
